import { CreateTagRequest, TagType } from './types';
import { FC, useState } from 'react';
import { PunchupTagsContainer, RemoveButton, TagCard, TagName } from './TagsSection.styles';
import { addTagsToEvent, createTag, removeTagsFromEvent } from './services';
import { useHistory, useParams } from 'react-router-dom';

import { CreateTagModal } from './components/CreateTagModal';
import { Divider } from 'features/common/components/Divider';
import { IdParamsType } from 'utils/types';
import { ROUTES } from 'features/routes';
import { SectionTitle } from 'features/common/components/SectionTitle';
import { TYPES } from 'hooks/useToastNotification/constants';
import useGetEvent from 'features/common/hooks/useGetEvent';
import { useResponsiveScreen } from 'hooks/useWindowSize';
import { useTagsApi } from './hooks/useTagsApi';
import { useToastNotification } from 'hooks/useToastNotification';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

const MAX_TAGS = 10;

const TagsSection: FC = () => {
	const { t } = useTranslation();
	const { id } = useParams<IdParamsType>();
	const { push } = useHistory();
	const isLargeScreen = useResponsiveScreen();
	const { showNotification } = useToastNotification();
	const { event } = useGetEvent();
	const { refetch: refetchTags } = useTagsApi();

	const [showCreateModal, setShowCreateModal] = useState(false);

	const eventTags = event?.tags || [];

	// Normalize tags to ensure they have lowercase 'id' property
	const normalizedTags = eventTags.map((tag) => ({
		...tag,
		id: tag.id || (tag as any).ID,
	}));

	const selectedTagIds = normalizedTags.map((tag) => tag.id);
	const canAddMore = selectedTagIds.length < MAX_TAGS;

	const handleRemoveTag = async (tagId: number) => {
		try {
			await removeTagsFromEvent(id, { tag_ids: [tagId] });
			showNotification({ message: t('tags-removed-successfully'), type: TYPES.SUCCESS });
			// Trigger refetch of event data
			window.location.reload();
		} catch (error) {
			showNotification({ message: t('tags-remove-error'), type: TYPES.ERROR });
		}
	};

	const handleCreateTag = async (tagData: CreateTagRequest) => {
		try {
			const newTag = await createTag(tagData);
			await refetchTags();
			showNotification({ message: t('tags-created-successfully'), type: TYPES.SUCCESS });

			// Add the newly created tag to the event
			await addTagsToEvent(id, { tag_ids: [newTag.id] });
			window.location.reload();
		} catch (error) {
			showNotification({ message: t('tags-create-error'), type: TYPES.ERROR });
		}
	};

	const onClickAddNew = () => {
		if (canAddMore) {
			push(ROUTES.DASHBOARD_SELECT_TAGS.replace(':id', id));
		} else {
			showNotification({ message: t('tags-max-limit-reached'), type: TYPES.INFO });
		}
	};

	return (
		<>
			<SectionTitle title={t('tags-event-tags')} buttonLabel={t('tags-add-tag')} onClick={onClickAddNew} />

			<PunchupTagsContainer data-cy="tags-section">
				{normalizedTags.length > 0 ? (
					normalizedTags.map((tag) => (
						<TagCard key={tag.id}>
							<TagName>{tag.name}</TagName>
							<RemoveButton
								onClick={(e) => {
									e.stopPropagation();
									handleRemoveTag(tag.id);
								}}
								title={t('tags-remove-tag')}
							>
								<X size={18} />
							</RemoveButton>
						</TagCard>
					))
				) : (
					<div style={{ padding: 24, color: '#888', width: '100%', textAlign: 'center' }}>
						{t('tags-no-tags-assigned')}
					</div>
				)}
			</PunchupTagsContainer>

			{!isLargeScreen && <Divider />}

			{showCreateModal && (
				<CreateTagModal onClose={() => setShowCreateModal(false)} onSubmit={handleCreateTag} tagType={TagType.EVENT} />
			)}
		</>
	);
};

export { TagsSection };
